import { createFileRoute, Link } from "@tanstack/react-router";
import { Car, Clock, MapPin, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/niagara-falls-day-trip-from-crystal-beach")({
  head: () => ({
    meta: [
      { title: "Niagara Falls Day Trip from Crystal Beach - Malak BnB Resort" },
      { name: "description", content: "Niagara Falls is a 30 minute drive from Crystal Beach. How to plan the day trip, and why Malak BnB Resort makes a quieter base than staying at the falls." },
      { property: "og:title", content: "Niagara Falls Day Trip from Crystal Beach - Malak BnB Resort" },
      { property: "og:description", content: "Plan a Niagara Falls day trip from a quieter Crystal Beach base, 30 minutes away at Malak BnB Resort." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/niagara-falls-day-trip-from-crystal-beach" },
      { property: "og:image", content: "/images/niagara-falls.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Niagara Falls Day Trip from Crystal Beach", path: "/blog/niagara-falls-day-trip-from-crystal-beach" },
        ]),
      },
    ],
    links: [canonical("/blog/niagara-falls-day-trip-from-crystal-beach")],
  }),
  component: NiagaraFallsPage,
});

const FAQ = [
  {
    q: "Is Malak BnB Resort close to Niagara Falls?",
    a: "Malak BnB Resort is approximately 30 minutes by car from Niagara Falls. Many guests use Crystal Beach as a quieter, more affordable base for a Niagara Falls day trip, avoiding the busy tourist core while still reaching the falls in under half an hour.",
  },
  {
    q: "How much time should I plan for a Niagara Falls day trip from Crystal Beach?",
    a: "A half day covers the main viewpoints along the Niagara Parkway. A full day allows time for an attraction like Journey Behind the Falls or a boat tour, plus a meal in the area, before the 30 minute drive back to Malak BnB Resort.",
  },
  {
    q: "Is it better to stay in Crystal Beach or right at Niagara Falls?",
    a: "It depends on what you want from the trip. Staying at Malak BnB Resort in Crystal Beach means quieter evenings, easy access to Bay Beach, and rates that are typically lower than hotels directly at the falls, at the cost of a short drive each way.",
  },
  {
    q: "What is the best time of day to visit Niagara Falls from Crystal Beach?",
    a: "Arriving by mid-morning avoids the worst of the crowds and parking pressure. Many visitors also return in the early evening for the illumination of the falls after dark, then make the 30 minute drive back to Crystal Beach for the night.",
  },
];

function NiagaraFallsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/niagara-falls.webp"
          alt="Niagara Falls viewed from the Canadian side"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Niagara Falls Day Trip</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">30 Minutes Away</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Visiting Niagara Falls While Staying in Crystal Beach
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Niagara Falls draws a crowd, and staying right in the middle of it is not for everyone. A lot of
              visitors have figured out a better version of the trip: stay somewhere quieter, and drive in for the
              day. Crystal Beach is one of the better places to do that from, close enough that the falls are never
              more than half an hour away.
            </p>
            <p>
              Malak BnB Resort sits at 264 Ridge Road South, about a 30 minute drive from Niagara Falls along the
              Niagara Parkway. You get the falls without paying tourist-core hotel prices, and you get your evenings
              back in a lakeside town instead of a strip lined with attractions.
            </p>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Photo by Ali Soheil via{" "}
            <a href="https://www.pexels.com/photo/majestic-view-of-niagara-falls-from-canada-35119417/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* Distance */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <Car className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">30 Minutes</p>
              <p className="text-sm text-muted-foreground mt-1">Drive from Malak BnB Resort to Niagara Falls</p>
            </div>
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <Clock className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">Half or Full Day</p>
              <p className="text-sm text-muted-foreground mt-1">Enough time for the main viewpoints, or more with an attraction added on</p>
            </div>
            <div className="bg-background rounded-2xl p-6 border border-border/50 text-center">
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <p className="font-heading text-lg text-foreground">Niagara Parkway</p>
              <p className="text-sm text-muted-foreground mt-1">A scenic drive along the Niagara River most of the way</p>
            </div>
          </div>
        </div>
      </section>

      {/* Planning */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Planning the Day</p>
          <h2 className="text-3xl font-heading text-foreground mb-6">What to See and How Long to Stay</h2>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              The core of any Niagara Falls visit is the view itself: Horseshoe Falls from the Canadian side, best
              seen from the promenade along the Niagara Parkway. If you want to get closer to the water, Journey
              Behind the Falls and the boat tours near the base of the falls both add a couple of hours to the trip.
            </p>
            <p>
              For a half day trip, arrive by mid-morning, walk the falls, grab lunch nearby, and head back to
              Crystal Beach in the early afternoon while the pool is still warm. For a fuller day, add one paid
              attraction and stay through the evening illumination before making the drive back.
            </p>
          </div>
        </div>
      </section>

      {/* Why Crystal Beach */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/hero-exterior.webp" alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Why Base Yourself Here</p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Quieter Evening After a Busy Day
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Crystal Beach gives you the opposite of the Niagara Falls tourist core. Malak BnB Resort is a
                family-owned bed and breakfast five minutes from Bay Beach, with an outdoor heated pool and
                continental breakfast every morning. Come back from the falls, swim, and fall asleep somewhere
                quiet instead of a strip lined with lights.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                  Request to Book
                </Link>
                <Link to="/rooms" className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Planning Your Niagara Falls Trip</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Base your Niagara Falls trip at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
